import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Store, Search, X, ChevronLeft, ChevronRight, Plus, List, Tag, CalendarDays, SlidersHorizontal, Bookmark, BookmarkCheck, ChevronDown, Clock, Sparkles, Bell, Filter } from 'lucide-react';
import api from '../services/api';
import MarketplaceCard from '../components/MarketplaceCard';
import AddToMarketplaceModal from '../components/AddToMarketplaceModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { FUEL_FILTER_PILLS, TRANSMISSION_FILTER_PILLS } from '../constants/vehicleFields';
import { MARKETPLACE_SORT_OPTIONS, BUDGET_PILLS } from '../constants/marketplace';

// ─── Dual Range Slider Component (touch-friendly) ───────────────────
const RangeSlider = ({ label, min, max, valueMin, valueMax, step = 1, onChange, formatValue }) => {
  const trackRef = useRef(null);
  const stateRef = useRef({ valueMin, valueMax, onChange });
  stateRef.current = { valueMin, valueMax, onChange };
  const format = formatValue || ((v) => v.toLocaleString('fr-BE'));
  const [dragging, setDragging] = useState(null);

  const getPercent = (val) => ((val - min) / (max - min)) * 100;

  const getValueFromPosition = useCallback((clientX) => {
    if (!trackRef.current) return min;
    const rect = trackRef.current.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.round((min + percent * (max - min)) / step) * step;
  }, [min, max, step]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e) => {
      e.preventDefault();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const value = getValueFromPosition(clientX);
      const { valueMin: vMin, valueMax: vMax, onChange: cb } = stateRef.current;
      if (dragging === 'min') {
        cb(Math.min(value, vMax - step), vMax);
      } else {
        cb(vMin, Math.max(value, vMin + step));
      }
    };
    const handleEnd = () => setDragging(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchmove', handleMove, { passive: false });
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [dragging, getValueFromPosition, step]);

  const handleTrackClick = (e) => {
    const value = getValueFromPosition(e.clientX);
    const distToMin = Math.abs(value - valueMin);
    const distToMax = Math.abs(value - valueMax);
    if (distToMin <= distToMax) {
      onChange(Math.min(value, valueMax - step), valueMax);
    } else {
      onChange(valueMin, Math.max(value, valueMin + step));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm font-medium text-label">{label}</label>
        <span className="text-sm text-carmin font-medium">
          {format(valueMin)} — {format(valueMax)}
        </span>
      </div>
      <div
        ref={trackRef}
        className="relative h-10 flex items-center cursor-pointer select-none"
        style={{ touchAction: 'none' }}
        onClick={handleTrackClick}
      >
        <div className="absolute inset-x-0 h-2 bg-dark-hover rounded-full" />
        <div
          className="absolute h-2 bg-carmin rounded-full"
          style={{
            left: `${getPercent(valueMin)}%`,
            width: `${getPercent(valueMax) - getPercent(valueMin)}%`,
          }}
        />
        {/* Min thumb */}
        <div
          className={`absolute w-7 h-7 bg-dark-card border-2 border-carmin rounded-full shadow-md -translate-x-1/2 z-10 transition-transform ${dragging === 'min' ? 'scale-125' : 'hover:scale-110'}`}
          style={{ left: `${getPercent(valueMin)}%`, cursor: 'grab' }}
          onMouseDown={(e) => { e.stopPropagation(); setDragging('min'); }}
          onTouchStart={(e) => { e.stopPropagation(); setDragging('min'); }}
        />
        {/* Max thumb */}
        <div
          className={`absolute w-7 h-7 bg-dark-card border-2 border-carmin rounded-full shadow-md -translate-x-1/2 z-10 transition-transform ${dragging === 'max' ? 'scale-125' : 'hover:scale-110'}`}
          style={{ left: `${getPercent(valueMax)}%`, cursor: 'grab' }}
          onMouseDown={(e) => { e.stopPropagation(); setDragging('max'); }}
          onTouchStart={(e) => { e.stopPropagation(); setDragging('max'); }}
        />
      </div>
    </div>
  );
};

// ─── Quick Filter Pill ───────────────────────────────────────────────
const FilterPill = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all border ${
      active
        ? 'bg-carmin text-white border-carmin'
        : 'bg-dark-card text-secondary border-dark-border hover:border-carmin/50 hover:text-carmin'
    }`}
  >
    {label}
    {count !== undefined && (
      <span className={`text-[10px] ${active ? 'text-white/70' : 'text-muted'}`}>({count})</span>
    )}
  </button>
);

// ─── Sort Chip ───────────────────────────────────────────────────────
const SortChip = ({ label, value, active, onClick }) => (
  <button
    onClick={() => onClick(value)}
    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
      active
        ? 'bg-theme-inverted text-theme-inverted'
        : 'bg-dark-card text-secondary border border-dark-border'
    }`}
  >
    {label}
  </button>
);

// ─── Active Filter Tag ───────────────────────────────────────────────
const ActiveTag = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-carmin-50 text-carmin rounded-full text-xs font-medium">
    {label}
    <button onClick={onRemove} className="ml-0.5 hover:text-carmin-light">
      <X className="h-3 w-3" />
    </button>
  </span>
);

// ─── Save Search Modal ───────────────────────────────────────────────
const SaveSearchModal = ({ filters, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const filterPayload = {};
      if (filters.search) filterPayload.search = filters.search;
      if (filters.make) filterPayload.make = filters.make;
      if (filters.fuel_type) filterPayload.fuel_type = filters.fuel_type;
      if (filters.transmission) filterPayload.transmission = filters.transmission;
      if (filters.min_price) filterPayload.min_price = filters.min_price;
      if (filters.max_price) filterPayload.max_price = filters.max_price;
      if (filters.min_year) filterPayload.min_year = filters.min_year;
      if (filters.max_year) filterPayload.max_year = filters.max_year;
      if (filters.max_mileage) filterPayload.max_mileage = filters.max_mileage;

      await api.post('/marketplace/searches/saved', {
        name: name || null,
        filters: filterPayload,
        notify_email: notifyEmail,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-dark-card rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
          <Bell className="h-5 w-5 text-carmin" />
          Sauvegarder cette recherche
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-label mb-1">Nom (optionnel)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: BMW Diesel < 20k"
              className="w-full bg-dark-input border border-dark-border rounded-lg px-3 py-2 text-sm text-primary placeholder-muted focus:ring-2 focus:ring-carmin focus:border-carmin"
            />
          </div>
          <div className="flex items-center justify-between p-3 bg-dark-hover rounded-lg">
            <div>
              <p className="text-sm font-medium text-label">Notifications par email</p>
              <p className="text-xs text-muted">Recevez un email quand de nouvelles annonces correspondent</p>
            </div>
            <button
              type="button"
              onClick={() => setNotifyEmail(!notifyEmail)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                notifyEmail ? 'bg-carmin' : 'bg-dark-hover'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ${
                notifyEmail ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 border border-dark-border rounded-lg text-sm font-medium text-label hover:bg-dark-hover">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 bg-carmin text-white rounded-lg text-sm font-medium hover:bg-carmin-light disabled:opacity-50"
          >
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Saved Searches Dropdown ─────────────────────────────────────────
const SavedSearchesDropdown = ({ onApply, onClose }) => {
  const [searches, setSearches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSearches();
  }, []);

  const loadSearches = async () => {
    try {
      const res = await api.get('/marketplace/searches/saved');
      setSearches(res.data.searches || []);
    } catch (err) {
      console.error('Error loading saved searches:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/marketplace/searches/saved/${id}`);
      setSearches(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Error deleting saved search:', err);
    }
  };

  if (loading) return <div className="p-4 text-center text-sm text-muted">Chargement...</div>;
  if (searches.length === 0) return <div className="p-4 text-center text-sm text-muted">Aucune recherche sauvegardée</div>;

  return (
    <div className="space-y-1">
      {searches.map(s => (
        <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-dark-hover group">
          <button
            onClick={() => { onApply(s.filters); onClose(); }}
            className="flex-1 text-left"
          >
            <p className="text-sm font-medium text-label truncate">{s.name}</p>
            <p className="text-xs text-muted">
              {s.match_count > 0 && <span className="text-carmin font-medium">{s.match_count} matchs</span>}
              {s.notify_email && <span className="ml-1 text-carmin">• Notifications actives</span>}
            </p>
          </button>
          <button
            onClick={() => handleDelete(s.id)}
            className="p-1 text-muted hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
};

// ─── Filter Bottom Sheet (Mobile) ────────────────────────────────────
const FilterSheet = ({ show, onClose, children }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute bottom-0 left-0 right-0 bg-dark-card rounded-t-2xl max-h-[85vh] overflow-y-auto animate-slide-up">
        <div className="sticky top-0 bg-dark-card p-4 border-b border-dark-border flex items-center justify-between">
          <h3 className="text-lg font-bold text-primary">Filtres avancés</h3>
          <button onClick={onClose} className="p-2 text-secondary hover:text-label">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 pb-8">
          {children}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN MARKETPLACE COMPONENT
// ═══════════════════════════════════════════════════════════════════════

const SORT_OPTIONS = MARKETPLACE_SORT_OPTIONS;
const FUEL_PILLS = FUEL_FILTER_PILLS;
const TRANSMISSION_PILLS = TRANSMISSION_FILTER_PILLS;

const Marketplace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dealerFilter = searchParams.get('dealer') || '';
  const [dealerName, setDealerName] = useState('');
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableFilters, setAvailableFilters] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [showAddModal, setShowAddModal] = useState(false);

  // Search & filter state
  const [search, setSearch] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedFuel, setSelectedFuel] = useState('');
  const [selectedTransmission, setSelectedTransmission] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState([0, 200000]);
  const [yearRange, setYearRange] = useState([2010, 2026]);
  const [mileageMax, setMileageMax] = useState(300000);
  const [activeBudgetPill, setActiveBudgetPill] = useState(null);

  // UI state
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showDesktopFilters, setShowDesktopFilters] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showSavedDropdown, setShowSavedDropdown] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Derived filter state
  const filterBounds = {
    minPrice: availableFilters?.min_price || 0,
    maxPrice: availableFilters?.max_price || 200000,
    minYear: availableFilters?.min_year || 2000,
    maxYear: availableFilters?.max_year || 2026,
  };

  // Initialize ranges from filters when loaded
  useEffect(() => {
    if (availableFilters) {
      setPriceRange([availableFilters.min_price || 0, availableFilters.max_price || 200000]);
      setYearRange([availableFilters.min_year || 2010, availableFilters.max_year || 2026]);
    }
  }, [availableFilters]);

  const loadFilters = async () => {
    try {
      const response = await api.get('/marketplace/filters');
      setAvailableFilters(response.data);
    } catch (err) { /* optional */ }
  };

  const loadRecentSearches = async () => {
    try {
      const res = await api.get('/marketplace/searches/recent?limit=5');
      setRecentSearches(res.data.searches || []);
    } catch (err) { /* optional */ }
  };

  const buildFilters = useCallback(() => {
    const filters = {};
    if (search) filters.search = search;
    if (selectedMake) filters.make = selectedMake;
    if (selectedFuel) filters.fuel_type = selectedFuel;
    if (selectedTransmission) filters.transmission = selectedTransmission;
    if (priceRange[0] > (filterBounds.minPrice || 0)) filters.min_price = priceRange[0];
    if (priceRange[1] < (filterBounds.maxPrice || 200000)) filters.max_price = priceRange[1];
    if (yearRange[0] > (filterBounds.minYear || 2000)) filters.min_year = yearRange[0];
    if (yearRange[1] < (filterBounds.maxYear || 2026)) filters.max_year = yearRange[1];
    if (mileageMax < 300000) filters.max_mileage = mileageMax;
    if (dealerFilter) filters.dealer_id = dealerFilter;
    return filters;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedMake, selectedFuel, selectedTransmission, priceRange, yearRange, mileageMax, dealerFilter]);

  const loadListings = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const filters = buildFilters();
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('sort_by', sortBy);
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.set(k, v);
      });

      const response = await api.get(`/marketplace/listings?${params.toString()}`);
      setListings(response.data.listings || []);
      if (dealerFilter && response.data.listings?.length > 0 && !dealerName) {
        setDealerName(response.data.listings[0].dealer?.display_name || '');
      }
      setPagination({
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages,
      });
    } catch (err) {
      setError('Erreur lors du chargement de la marketplace');
    } finally {
      setLoading(false);
    }
  }, [buildFilters, sortBy, dealerFilter, dealerName]);

  useEffect(() => { loadFilters(); loadRecentSearches(); }, []);
  useEffect(() => { loadListings(1); }, [loadListings]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ─── Handlers ────────────────────────────────────────────────────

  const handleSearchChange = (val) => {
    setSearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (val.length >= 2) {
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleFuelToggle = (fuel) => {
    setSelectedFuel(prev => prev === fuel ? '' : fuel);
  };

  const handleTransmissionToggle = (trans) => {
    setSelectedTransmission(prev => prev === trans ? '' : trans);
  };

  const handleBudgetPill = (index) => {
    if (activeBudgetPill === index) {
      setActiveBudgetPill(null);
      if (availableFilters) {
        setPriceRange([availableFilters.min_price || 0, availableFilters.max_price || 200000]);
      }
    } else {
      setActiveBudgetPill(index);
      const [min, max] = BUDGET_PILLS[index].value;
      setPriceRange([min, max || filterBounds.maxPrice || 200000]);
    }
  };

  const handleMakeSuggestion = (make) => {
    setSelectedMake(make);
    setSearch('');
    setShowSuggestions(false);
  };

  const handleApplySavedFilters = (filters) => {
    if (filters.search) setSearch(filters.search);
    if (filters.make) setSelectedMake(filters.make);
    if (filters.fuel_type) setSelectedFuel(filters.fuel_type);
    if (filters.transmission) setSelectedTransmission(filters.transmission);
    if (filters.min_price || filters.max_price) {
      setPriceRange([filters.min_price || 0, filters.max_price || filterBounds.maxPrice]);
    }
    if (filters.min_year || filters.max_year) {
      setYearRange([filters.min_year || filterBounds.minYear, filters.max_year || filterBounds.maxYear]);
    }
    if (filters.max_mileage) setMileageMax(filters.max_mileage);
  };

  const clearAllFilters = () => {
    setSearch('');
    setSelectedMake('');
    setSelectedFuel('');
    setSelectedTransmission('');
    setSortBy('newest');
    setActiveBudgetPill(null);
    setMileageMax(300000);
    if (availableFilters) {
      setPriceRange([availableFilters.min_price || 0, availableFilters.max_price || 200000]);
      setYearRange([availableFilters.min_year || 2010, availableFilters.max_year || 2026]);
    }
  };

  // ─── Active filter tags ──────────────────────────────────────────
  const activeFilterTags = [];
  if (search) activeFilterTags.push({ label: `"${search}"`, clear: () => setSearch('') });
  if (selectedMake) activeFilterTags.push({ label: selectedMake, clear: () => setSelectedMake('') });
  if (selectedFuel) activeFilterTags.push({ label: selectedFuel, clear: () => setSelectedFuel('') });
  if (selectedTransmission) activeFilterTags.push({ label: selectedTransmission === 'automatic' ? 'Automatique' : 'Manuelle', clear: () => setSelectedTransmission('') });
  if (priceRange[0] > (filterBounds.minPrice || 0) || priceRange[1] < (filterBounds.maxPrice || 200000)) {
    activeFilterTags.push({
      label: `${priceRange[0].toLocaleString('fr-BE')} - ${priceRange[1].toLocaleString('fr-BE')} €`,
      clear: () => { setPriceRange([filterBounds.minPrice || 0, filterBounds.maxPrice || 200000]); setActiveBudgetPill(null); }
    });
  }
  if (yearRange[0] > (filterBounds.minYear || 2000) || yearRange[1] < (filterBounds.maxYear || 2026)) {
    activeFilterTags.push({
      label: `${yearRange[0]} - ${yearRange[1]}`,
      clear: () => setYearRange([filterBounds.minYear || 2010, filterBounds.maxYear || 2026])
    });
  }
  if (mileageMax < 300000) {
    activeFilterTags.push({
      label: `< ${mileageMax.toLocaleString('fr-BE')} km`,
      clear: () => setMileageMax(300000)
    });
  }

  const hasActiveFilters = activeFilterTags.length > 0;
  const currentFilters = buildFilters();

  // ─── Advanced filters content (shared between desktop & mobile) ──
  const advancedFiltersContent = (
    <div className="space-y-6">
      {/* Make dropdown */}
      <div>
        <label className="block text-sm font-medium text-label mb-2">Marque</label>
        <select
          value={selectedMake}
          onChange={(e) => setSelectedMake(e.target.value)}
          className="w-full bg-dark-input border border-dark-border rounded-lg px-3 py-2.5 text-sm text-primary focus:ring-2 focus:ring-carmin focus:border-carmin"
        >
          <option value="">Toutes marques</option>
          {availableFilters?.makes?.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
      </div>

      {/* Price range */}
      <RangeSlider
        label="Budget"
        min={filterBounds.minPrice || 0}
        max={filterBounds.maxPrice || 200000}
        valueMin={priceRange[0]}
        valueMax={priceRange[1]}
        step={1000}
        onChange={(min, max) => { setPriceRange([min, max]); setActiveBudgetPill(null); }}
        formatValue={(v) => `${(v / 1000).toFixed(0)}k €`}
      />

      {/* Year range */}
      <RangeSlider
        label="Année"
        min={filterBounds.minYear || 2000}
        max={filterBounds.maxYear || 2026}
        valueMin={yearRange[0]}
        valueMax={yearRange[1]}
        step={1}
        onChange={(min, max) => setYearRange([min, max])}
      />

      {/* Mileage */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-label">Kilométrage max</label>
          <span className="text-sm text-carmin font-medium">
            {mileageMax >= 300000 ? 'Illimité' : `${mileageMax.toLocaleString('fr-BE')} km`}
          </span>
        </div>
        <input
          type="range"
          min={0} max={300000} step={10000}
          value={mileageMax}
          onChange={(e) => setMileageMax(Number(e.target.value))}
          className="w-full h-2 bg-dark-hover rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-7 [&::-webkit-slider-thumb]:h-7 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-dark-card [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-carmin [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-7 [&::-moz-range-thumb]:h-7 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-dark-card [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-carmin [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:appearance-none"
          style={{ touchAction: 'none' }}
        />
        <div className="flex justify-between text-xs text-muted mt-1">
          <span>0 km</span>
          <span>150k</span>
          <span>300k+</span>
        </div>
      </div>
    </div>
  );

  // Matching makes for search suggestions
  const matchingMakes = search.length >= 2 && availableFilters?.makes
    ? availableFilters.makes.filter(m => m.toLowerCase().includes(search.toLowerCase()))
    : [];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
              <Store className="h-7 w-7 text-carmin" />
              Marketplace Pro
            </h1>
            <p className="text-sm text-muted mt-1 hidden sm:block">Réseau B2B entre professionnels de l'automobile</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2.5 bg-carmin text-white rounded-lg hover:bg-carmin-light text-sm font-medium shadow-sm"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">Ajouter une annonce</span>
            <span className="sm:hidden">Ajouter</span>
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Link
            to="/marketplace/offers"
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-dark-card border-2 border-dark-border text-label rounded-lg hover:bg-dark-hover hover:border-carmin/50 text-sm font-medium transition-colors"
          >
            <Tag className="h-4 w-4" />
            <span>Mes offres</span>
          </Link>
          <Link
            to="/marketplace/appointments"
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-dark-card border-2 border-dark-border text-label rounded-lg hover:bg-dark-hover hover:border-carmin/50 text-sm font-medium transition-colors"
          >
            <CalendarDays className="h-4 w-4" />
            <span>Mes RDV</span>
          </Link>
          <Link
            to="/marketplace/my-listings"
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 bg-dark-card border-2 border-dark-border text-label rounded-lg hover:bg-dark-hover hover:border-carmin/50 text-sm font-medium transition-colors"
          >
            <List className="h-4 w-4" />
            <span className="hidden sm:inline">Mes annonces</span>
            <span className="sm:hidden">Annonces</span>
          </Link>
        </div>
      </div>

      {/* Dealer filter banner */}
      {dealerFilter && (
        <div className="bg-carmin-50 border border-carmin/30 rounded-lg p-3 mb-4 flex items-center justify-between">
          <p className="text-sm text-carmin">
            Annonces de <span className="font-semibold">{dealerName || 'ce vendeur'}</span>
          </p>
          <button
            onClick={() => { setSearchParams({}); setDealerName(''); }}
            className="inline-flex items-center text-sm text-carmin hover:text-carmin-light"
          >
            <X className="h-4 w-4 mr-1" /> Voir tout
          </button>
        </div>
      )}

      {/* ─── Smart Search Bar (sticky) ──────────────────────────────── */}
      <div className="sticky top-0 z-30 bg-dark-bg -mx-4 px-4 sm:-mx-6 sm:px-6 py-3 mb-2">
        <div className="relative" ref={searchRef}>
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted" />
          <input
            type="text"
            placeholder="Rechercher marque, modèle, version..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            onFocus={() => { if (search.length >= 2 || recentSearches.length) setShowSuggestions(true); }}
            className="w-full pl-12 pr-10 py-3.5 bg-dark-input border border-dark-border rounded-2xl text-sm text-primary focus:ring-2 focus:ring-carmin focus:border-carmin shadow-sm placeholder-muted"
          />
          {search && (
            <button
              onClick={() => { setSearch(''); setShowSuggestions(false); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-muted hover:text-label"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {/* Search suggestions dropdown */}
          {showSuggestions && (
            <div ref={suggestionsRef} className="absolute left-0 right-0 top-full mt-1 bg-dark-card rounded-xl shadow-lg border border-dark-border overflow-hidden z-40">
              {matchingMakes.length > 0 && (
                <div className="p-2">
                  <p className="text-xs text-muted px-2 mb-1">Marques</p>
                  {matchingMakes.slice(0, 5).map(m => (
                    <button
                      key={m}
                      onClick={() => handleMakeSuggestion(m)}
                      className="w-full text-left px-3 py-2 text-sm text-label hover:bg-dark-hover rounded-lg flex items-center gap-2"
                    >
                      <Search className="h-3.5 w-3.5 text-muted" />
                      {m}
                    </button>
                  ))}
                </div>
              )}
              {recentSearches.length > 0 && search.length < 2 && (
                <div className="p-2 border-t border-dark-border">
                  <p className="text-xs text-muted px-2 mb-1">Recherches récentes</p>
                  {recentSearches.slice(0, 5).map((s) => {
                    const filterLabel = Object.entries(s.filters)
                      .filter(([, v]) => v)
                      .map(([k, v]) => k === 'search' ? `"${v}"` : v)
                      .join(', ');
                    return (
                      <button
                        key={s.id}
                        onClick={() => { handleApplySavedFilters(s.filters); setShowSuggestions(false); }}
                        className="w-full text-left px-3 py-2 text-sm text-secondary hover:bg-dark-hover rounded-lg flex items-center gap-2"
                      >
                        <Clock className="h-3.5 w-3.5 text-muted" />
                        <span className="truncate">{filterLabel}</span>
                        <span className="ml-auto text-xs text-muted">{s.result_count} rés.</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {matchingMakes.length === 0 && search.length >= 2 && (
                <div className="p-3 text-center text-sm text-muted">
                  <Sparkles className="h-4 w-4 text-muted mx-auto mb-1" />
                  Recherche sur tous les véhicules...
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── Action Bar (save, saved, params) ────────────────────────── */}
      <div className="flex items-center gap-2 mb-3 -mx-4 px-4 sm:-mx-6 sm:px-6">
        {/* Save search - gold */}
        <button
          onClick={() => setShowSaveModal(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
        >
          <Bookmark className="h-3.5 w-3.5" />
          Sauvegarder
        </button>
        {/* Saved searches - gold */}
        <div className="relative">
          <button
            onClick={() => setShowSavedDropdown(!showSavedDropdown)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors"
          >
            <BookmarkCheck className="h-3.5 w-3.5" />
            Mes recherches
          </button>
          {showSavedDropdown && (
            <div className="absolute left-0 top-full mt-2 w-72 bg-dark-card rounded-xl shadow-lg border border-dark-border p-2 z-40">
              <SavedSearchesDropdown
                onApply={handleApplySavedFilters}
                onClose={() => setShowSavedDropdown(false)}
              />
            </div>
          )}
        </div>
        {/* Advanced filters */}
        <button
          onClick={() => {
            if (window.innerWidth >= 1024) {
              setShowDesktopFilters(!showDesktopFilters);
            } else {
              setShowFilterSheet(true);
            }
          }}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            showDesktopFilters
              ? 'bg-theme-inverted text-theme-inverted border-theme-inverted'
              : 'bg-dark-card text-label border-dark-border hover:bg-dark-hover'
          }`}
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Paramètres
        </button>
      </div>

      {/* ─── Row 1: Fuel Pills ──────────────────────────────────────── */}
      <div className="mb-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex gap-2 pb-1 flex-wrap">
          {FUEL_PILLS.map(f => (
            <FilterPill
              key={f.value}
              label={f.label}
              active={selectedFuel === f.value}
              onClick={() => handleFuelToggle(f.value)}
            />
          ))}
        </div>
      </div>

      {/* ─── Row 2: Transmission Pills ────────────────────────────────── */}
      <div className="mb-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex gap-2 pb-1 flex-wrap">
          {TRANSMISSION_PILLS.map(t => (
            <FilterPill
              key={t.value}
              label={t.label}
              active={selectedTransmission === t.value}
              onClick={() => handleTransmissionToggle(t.value)}
            />
          ))}
        </div>
      </div>

      {/* ─── Row 3: Budget Pills ──────────────────────────────────────── */}
      <div className="mb-3 overflow-x-auto scrollbar-hide -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex gap-2 pb-1 flex-wrap">
          {BUDGET_PILLS.map((b, i) => (
            <FilterPill
              key={i}
              label={b.label}
              active={activeBudgetPill === i}
              onClick={() => handleBudgetPill(i)}
            />
          ))}
        </div>
      </div>

      {/* ─── Row 4: Display Filters (with funnel icon) ────────────────── */}
      <div className="mb-3 -mx-4 px-4 sm:-mx-6 sm:px-6">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-muted flex-shrink-0" />
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-wrap">
            {SORT_OPTIONS.map(s => (
              <SortChip
                key={s.value}
                label={s.label}
                value={s.value}
                active={sortBy === s.value}
                onClick={setSortBy}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ─── Row 5: Vehicle Count ────────────────────────── */}
      <div className="mb-4 -mx-4 px-4 sm:-mx-6 sm:px-6">
        <p className="text-sm font-bold text-carmin">
          {pagination.total} véhicule{pagination.total !== 1 ? 's' : ''} trouvé{pagination.total !== 1 ? 's' : ''}
        </p>
      </div>

      {/* ─── Desktop Advanced Filters Panel ─────────────────────────── */}
      {showDesktopFilters && (
        <div className="hidden lg:block bg-dark-card rounded-xl border border-dark-border p-5 mb-4">
          <div className="grid grid-cols-3 gap-6">
            {advancedFiltersContent.props.children}
          </div>
        </div>
      )}

      {/* ─── Mobile Filter Bottom Sheet ─────────────────────────────── */}
      <FilterSheet show={showFilterSheet} onClose={() => setShowFilterSheet(false)}>
        {advancedFiltersContent}
        <button
          onClick={() => setShowFilterSheet(false)}
          className="w-full mt-6 py-3 bg-carmin text-white rounded-xl text-sm font-medium hover:bg-carmin-light"
        >
          Voir {pagination.total} résultat{pagination.total !== 1 ? 's' : ''}
        </button>
      </FilterSheet>

      {/* ─── Active Filter Tags ─────────────────────────────────────── */}
      {activeFilterTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {activeFilterTags.map((tag, i) => (
            <ActiveTag key={i} label={tag.label} onRemove={tag.clear} />
          ))}
          <button
            onClick={clearAllFilters}
            className="text-xs text-red-400 hover:text-red-300 font-medium ml-1"
          >
            Tout effacer
          </button>
        </div>
      )}

      {/* ─── Results ────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg mb-6 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="py-12"><LoadingSpinner size="lg" /></div>
      ) : listings.length === 0 ? (
        <div className="text-center py-16">
          <Store className="h-16 w-16 text-muted mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">Aucun véhicule trouvé</h3>
          <p className="text-secondary text-sm mb-4">
            {hasActiveFilters
              ? 'Essayez de modifier vos filtres ou sauvegardez cette recherche pour être notifié.'
              : 'Soyez le premier à mettre un véhicule en vente !'
            }
          </p>
          {hasActiveFilters && (
            <div className="flex gap-3 justify-center">
              <button onClick={clearAllFilters} className="text-sm text-carmin hover:text-carmin-light font-medium">
                Réinitialiser les filtres
              </button>
              <button
                onClick={() => setShowSaveModal(true)}
                className="inline-flex items-center gap-1.5 text-sm text-carmin hover:text-carmin-light font-medium"
              >
                <Bell className="h-4 w-4" /> Créer une alerte
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map(listing => (
              <MarketplaceCard key={listing.id} listing={listing} isOwn={listing.is_own} />
            ))}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <button
                onClick={() => loadListings(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="inline-flex items-center px-4 py-2.5 bg-dark-card border border-dark-border rounded-lg text-sm font-medium text-label disabled:opacity-40 disabled:cursor-not-allowed hover:bg-dark-hover transition-colors"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Précédent
              </button>
              <span className="text-sm text-secondary font-medium">
                {pagination.page} / {pagination.total_pages}
              </span>
              <button
                onClick={() => loadListings(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages}
                className="inline-flex items-center px-4 py-2.5 bg-dark-card border border-dark-border rounded-lg text-sm font-medium text-label disabled:opacity-40 disabled:cursor-not-allowed hover:bg-dark-hover transition-colors"
              >
                Suivant <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          )}
        </>
      )}

      {/* ─── Modals ─────────────────────────────────────────────────── */}
      {showAddModal && (
        <AddToMarketplaceModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => { loadListings(1); loadFilters(); }}
        />
      )}
      {showSaveModal && (
        <SaveSearchModal
          filters={currentFilters}
          onClose={() => setShowSaveModal(false)}
          onSaved={loadRecentSearches}
        />
      )}
    </div>
  );
};

export default Marketplace;
